import { useCallback, useMemo } from 'react';

import { PermissionGroup } from 'src/constants/modulePermission';
import { SECTION } from 'src/constants/section';
import settings from 'src/settings';
import type {
  ModuleIdTypeMapProps,
  ModulePermission,
} from 'src/types/modulePermission';
import type { User } from 'src/types/user';
import useFetchSurveyList from 'src/components/survey/SurveyContext/ListContext/useFetchSurveyList';

import { useUserContext } from './index';

export interface UserPermissions {
  activeUserPermissionsInCurrentSurvey: readonly ModulePermission[];
  adminUserPermissions: readonly ModulePermission[];
  moduleIdTypeMap: ModuleIdTypeMapProps;
  getPermissionForModuleType: (
    moduleType: number,
    includeInactivePermissions?: boolean,
  ) => ModulePermission | undefined;
  hasPermissionForModuleType: (moduleType: number) => boolean;
  getPermissionsForGroup: (
    group: PermissionGroup,
    includeInactivePermissions?: boolean,
  ) => ModulePermission[];
  hasReadOnlyPermissionForModuleType: (
    moduleType: number,
    surveyId?: number,
  ) => boolean;
  hasAdminPermissionForModuleType: (
    moduleType: number,
    survey_id?: number,
  ) => boolean;
  hasAdminPermissionForModuleTypeInAnySurvey: (moduleType: number) => boolean;
  hasWritePermissionForModuleType: (moduleType: number) => boolean;
  hasPermissionForModuleTypeInAnySurvey: (moduleType: number) => boolean;
  hasWritePermissionForModuleTypeInAnySurvey: (moduleType: number) => boolean;
  hasMyFollowUpAccess: () => boolean;
  getModuleIdForModuleType: (
    moduleType: number,
    surveyId?: number,
  ) => number | undefined;
}

export const findUserPermissionsForSurvey = (
  user?: User,
  survey_id?: number,
): readonly ModulePermission[] => {
  if (!!survey_id && !!user) {
    const surveyPermissionsForSurvey = user.surveyPermissions.find(
      (permission_per_survey) => permission_per_survey.survey_id === survey_id,
    );
    if (surveyPermissionsForSurvey) {
      return surveyPermissionsForSurvey.permissions;
    }
  }
  return [];
};

export const findCurrentSurveyPermissions = (
  user: User,
): readonly ModulePermission[] => {
  if (user.session.survey) {
    return findUserPermissionsForSurvey(user, user.session.survey.id);
  }
  return [];
};

const useUserPermissions = (): UserPermissions => {
  const { user } = useUserContext();
  const { data: surveys } = useFetchSurveyList(!!user) || {};
  const activeUserPermissionsInCurrentSurvey = useMemo(() => {
    if (!user) return [];
    const currentSurveyPermissions = findCurrentSurveyPermissions(user);
    if (user.is_superuser) return currentSurveyPermissions;
    if (user.isFrontendAdmin) {
      // frontend admins can access modules that are not explicitly excluded
      return currentSurveyPermissions.filter(
        (permission) => !permission.module.exclude_for_frontend_admin,
      );
    }
    return currentSurveyPermissions.filter(
      (permission) =>
        (permission.module.is_active ||
          permission.permission_group === PermissionGroup.ADMIN) &&
        permission.has_access,
    );
  }, [user]);

  // cases when users shall be able to access via module admin permission only
  // (i.e. without module being active / without user having active module access specifically)
  const adminUserPermissions = useMemo(() => {
    if (!user) return [];
    const currentSurveyPermissions = findCurrentSurveyPermissions(user);
    if (user.is_superuser) return currentSurveyPermissions;
    return currentSurveyPermissions.filter(
      (permission) => permission.permission_group === PermissionGroup.ADMIN,
    );
  }, [user]);

  const hasAdminPermissionForModuleType = useCallback(
    (moduleType: number, surveyId?: number) => {
      if (!user) return false;
      if (user?.is_superuser) return true;
      return findUserPermissionsForSurvey(user, surveyId).some(
        (permission) =>
          permission.module.module_type === moduleType &&
          permission.permission_group === PermissionGroup.ADMIN,
      );
    },
    [user],
  );
  const hasAdminPermissionForModuleTypeInAnySurvey = useCallback(
    (moduleType: number) => {
      if (!user) return false;
      if (user?.is_superuser) return true;
      return user.surveyPermissions.some((permissions_per_survey) =>
        permissions_per_survey.permissions.some(
          (permission) =>
            permission.module.module_type === moduleType &&
            permission.permission_group === PermissionGroup.ADMIN,
        ),
      );
    },
    [user],
  );

  // module ids and corresponding module types the user may currently access
  const moduleIdTypeMap = useMemo(
    () =>
      user
        ? findCurrentSurveyPermissions(user).reduce(
            (acc, permission) => ({
              ...acc,
              [permission.module.id]: permission.module.module_type,
            }),
            {},
          )
        : {},
    [user],
  );

  const hasPermissionForModuleType = useCallback(
    (moduleType: number) =>
      activeUserPermissionsInCurrentSurvey.some(
        (permission) => permission.module.module_type === moduleType,
      ),
    [activeUserPermissionsInCurrentSurvey],
  );

  const hasReadOnlyPermissionForModuleType = useCallback(
    (moduleType?: number, survey_id?: number) =>
      findUserPermissionsForSurvey(user || undefined, survey_id).some(
        (permission) =>
          permission.module.module_type === moduleType && permission.read_only,
      ),
    [user],
  );

  const hasWritePermissionForModuleType = useCallback(
    (moduleType: number) =>
      hasPermissionForModuleType(moduleType) &&
      !hasReadOnlyPermissionForModuleType(moduleType),
    [hasPermissionForModuleType, hasReadOnlyPermissionForModuleType],
  );

  const getPermissionForModuleType = useCallback(
    (moduleType: number, includeInactivePermissions?: boolean) => {
      const permissions =
        includeInactivePermissions && user
          ? findCurrentSurveyPermissions(user)
          : activeUserPermissionsInCurrentSurvey;

      return permissions.find(
        (permission) => permission.module.module_type === moduleType,
      );
    },
    [activeUserPermissionsInCurrentSurvey, user],
  );

  const getPermissionsForGroup = useCallback(
    (group: PermissionGroup, includeInactivePermissions?: boolean) => {
      const permissions =
        includeInactivePermissions && user
          ? findCurrentSurveyPermissions(user)
          : activeUserPermissionsInCurrentSurvey;

      return permissions.filter(
        (permission) => permission.permission_group === group,
      );
    },
    [activeUserPermissionsInCurrentSurvey, user],
  );

  const hasPermissionForModuleTypeInAnySurvey = useCallback(
    (moduleType: number) =>
      surveys
        ? surveys.some(({ accessibleModulePermissions }) =>
            accessibleModulePermissions.some(
              (permission) => permission.moduleType === moduleType,
            ),
          )
        : false,
    [surveys],
  );

  const hasWritePermissionForModuleTypeInAnySurvey = useCallback(
    (moduleType: number) =>
      surveys
        ? surveys.some(({ accessibleModulePermissions }) =>
            accessibleModulePermissions.some(
              (permission) =>
                permission.moduleType === moduleType && !permission.readOnly,
            ),
          )
        : false,
    [surveys],
  );

  const hasMyFollowUpAccess = useCallback(
    () =>
      hasPermissionForModuleTypeInAnySurvey(SECTION.FOLLOW_UP_ACTION) ||
      (hasPermissionForModuleTypeInAnySurvey(SECTION.FOLLOW_UP) &&
        settings.VITE_APP_IS_MY_FOLLOW_UP_UNLOCKED === 'true'),
    [surveys],
  );

  const getModuleIdForModuleType = useCallback(
    (moduleType: number, surveyId?: number): number | undefined =>
      surveys
        ?.find(({ id }) => id === surveyId)
        ?.accessibleModulePermissions.find(
          (permission) => permission.moduleType === moduleType,
        )?.moduleId as number | undefined,
    [surveys],
  );

  return useMemo(
    () => ({
      activeUserPermissionsInCurrentSurvey,
      adminUserPermissions,
      moduleIdTypeMap,
      getPermissionForModuleType,
      hasPermissionForModuleType,
      getPermissionsForGroup,
      hasReadOnlyPermissionForModuleType,
      hasWritePermissionForModuleType,
      hasPermissionForModuleTypeInAnySurvey,
      hasWritePermissionForModuleTypeInAnySurvey,
      hasMyFollowUpAccess,
      hasAdminPermissionForModuleType,
      hasAdminPermissionForModuleTypeInAnySurvey,
      getModuleIdForModuleType,
    }),
    [
      activeUserPermissionsInCurrentSurvey,
      adminUserPermissions,
      moduleIdTypeMap,
      getPermissionForModuleType,
      hasPermissionForModuleType,
      getPermissionsForGroup,
      hasReadOnlyPermissionForModuleType,
      hasWritePermissionForModuleType,
      hasPermissionForModuleTypeInAnySurvey,
      hasWritePermissionForModuleTypeInAnySurvey,
      hasMyFollowUpAccess,
      hasAdminPermissionForModuleType,
      hasAdminPermissionForModuleTypeInAnySurvey,
      getModuleIdForModuleType,
    ],
  );
};

export default useUserPermissions;
